/** @jsx jsx */
import * as React from 'react';
import type { PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { jsx, css } from '@compiled/react';
import type { State } from '@helpcenter/common-state-selectors';
import { isAssetsCustomFieldUserTimezoneFixEnabled, isAurumAssetsPcs446899FixEnabled } from '@helpcenter/feature-flags';
import { cloneDeep } from 'lodash';
import { Text } from '@atlaskit/primitives';
import type { OptionProps } from '@atlaskit/select';
import { components } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { CmdbObject, CmdbObjectAttribute } from '../../../rest/cmdb-object-picker';
import { convertToTimezone } from '../../utils/timezone-conversion.util';
import type { CmdbObjectPickerOption } from '../cmdb-object-picker-dumb';
import { MemoizedInsightIcon } from '../insight-icon';

export const getVisibleAttributeWithTimezoneSupport = (
    attributes: CmdbObjectAttribute[],
    attributesToDisplay: string[],
    timezone: State['persisted']['user']['timezone']
): CmdbObjectAttribute[] => {
    const notUndefined = (attr: CmdbObjectAttribute | undefined): attr is CmdbObjectAttribute => {
        return Boolean(attr);
    };

    const deepClonedAttributes: CmdbObjectAttribute[] = cloneDeep(attributes);

    return (
        attributesToDisplay
            .map((attrName) =>
                attrName === 'Label'
                    ? deepClonedAttributes.find((attr) => attr.objectTypeAttribute.label)
                    : deepClonedAttributes.find((attr) => attr.objectTypeAttribute.name === attrName)
            )
            // get only attributes that have valid displayValue
            .filter(notUndefined)
            .filter(
                (attribute) =>
                    attribute.objectAttributeValues.length > 0 &&
                    attribute.objectAttributeValues.every((value) => value && value.displayValue)
            )
            .map((attribute) => {
                if (attribute.objectTypeAttribute.defaultType?.name === 'DateTime') {
                    attribute.objectAttributeValues.forEach((value) => {
                        value.displayValue = value.value
                            ? convertToTimezone(value.value, timezone.id)
                            : value.displayValue;
                    });
                }

                return attribute;
            })
    );
};

export const BoldMatchingText: React.FC<{ match: string; children: string }> = ({ match, children }) => {
    if (match === '') {
        return <React.Fragment>{children}</React.Fragment>;
    }

    // we need to escape special characters to avoid getting them interpreted in the RegExp group
    const escapedSpecialCharsInMatchQuery = match.replace(/[\\.,*+?^${}()|[\]]/g, '\\$&');
    const parts = children.split(new RegExp(`(${escapedSpecialCharsInMatchQuery})`, 'gi'));
    return (
        <React.Fragment>
            {parts.map((part, i) => {
                if (part.toLowerCase() === match.toLowerCase()) {
                    // eslint-disable-next-line @atlaskit/design-system/use-primitives-text
                    return <strong key={i}>{part}</strong>;
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </React.Fragment>
    );
};

export const getVisibleAttributes = (
    attributes: CmdbObjectAttribute[],
    attributesToDisplay: string[]
): CmdbObjectAttribute[] => {
    const notUndefined = (attr: CmdbObjectAttribute | undefined): attr is CmdbObjectAttribute => {
        return Boolean(attr);
    };
    return (
        attributesToDisplay
            .map((attrName) =>
                attrName === 'Label'
                    ? attributes.find((attr) => attr.objectTypeAttribute.label)
                    : attributes.find((attr) => attr.objectTypeAttribute.name === attrName)
            )
            // get only attributes that have valid displayValue
            .filter(notUndefined)
            .filter(
                (attribute) =>
                    attribute.objectAttributeValues.length > 0 &&
                    attribute.objectAttributeValues.every((value) => value && value.displayValue)
            )
    );
};

export const createAttributeString = (attributes: CmdbObjectAttribute[]): string => {
    return attributes
        .map((attribute) => attribute.objectAttributeValues.map((value) => value.displayValue).join(', '))
        .join(' | ');
};

export const createLabelString = (attributes: CmdbObjectAttribute[], defaultLabel: string): string => {
    return attributes[0]?.objectAttributeValues.map((v) => v.displayValue).join(', ') || defaultLabel;
};

export type Props = OptionProps<CmdbObjectPickerOption>;

export const CmdbSelectOptionInternal = (optionProps: PropsWithChildren<Props>) => {
    const { data, innerProps, selectProps, timezone } = optionProps;
    const { inputValue, filterAttributes } = selectProps;
    const object = data.cmdbObject as CmdbObject;
    const visibleAttributes = isAssetsCustomFieldUserTimezoneFixEnabled()
        ? getVisibleAttributeWithTimezoneSupport(object.attributes, filterAttributes ?? [], timezone)
        : getVisibleAttributes(object.attributes, filterAttributes ?? []);
    const [, ...secondaryAttributes] = visibleAttributes;
    const labelAttribute = createLabelString(visibleAttributes, object.label);
    const secondaryAttributesDescription = createAttributeString(secondaryAttributes);
    const { Option } = components;
    const { ref, ...otherDivProps } = innerProps;

    return (
        <Option {...optionProps}>
            <div css={outerStyles} {...otherDivProps}>
                <div css={iconStyles}>
                    <MemoizedInsightIcon
                        {...object.avatar.mediaClientConfig}
                        {...(isAurumAssetsPcs446899FixEnabled() && { iconUrl: object.avatar.url48 })}
                    />
                </div>
                <div css={contentStyles}>
                    <Text size="medium" weight="medium">
                        <BoldMatchingText match={inputValue ?? ''}>{labelAttribute}</BoldMatchingText>
                    </Text>
                    <Text size="small">
                        <BoldMatchingText match={inputValue ?? ''}>{secondaryAttributesDescription}</BoldMatchingText>
                    </Text>
                </div>
            </div>
        </Option>
    );
};

const selector = createSelector(
    (state: State) => {
        return state.persisted.user.timezone;
    },
    (timezone) => ({
        timezone,
    })
);

export const CmdbSelectOption = connect(selector)(CmdbSelectOptionInternal);

const outerStyles = css({
    display: 'flex',
    paddingTop: token('space.100', '0.5rem'),
    paddingBottom: token('space.100', '0.5rem'),
    paddingLeft: token('space.200', '1rem'),
    paddingRight: token('space.200', '1rem'),
    '&:hover': {
        backgroundColor: token('elevation.surface.overlay.hovered', '#EBECF0'),
    },
});

const iconStyles = css({
    marginRight: token('space.150', '0.75rem'),
    flexShrink: 0,
});

const contentStyles = css({
    display: 'flex',
    flexDirection: 'column',
    paddingTop: token('space.025', '0.125rem'),
});
